import { DestroyRef, Directive, ElementRef, inject, Input } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { TranslateService } from "@ngx-translate/core";
import { BehaviorSubject, Subscription } from "rxjs";
import { SmartComponent } from "./smart-components/smart-component";
import { SmartComponentProperty } from "src/components/shared-components/class/smart-components/smart-component-property";
import {
  SmartComponentPropertyDetails,
  SmartComponentSummaryData,
} from "src/components/shared-components/class/smart-components/smart-component-property-details";
import { MqttClientService } from "src/service/mqtt-client.service";
import { SmartComponentService } from "src/components/shared-components/service/smart-component.service";
import { SmartComponentTheme } from "../enum/enumSmartComponentTheme";
import { ApplicationTheme } from "../enum/enumMisc";
import { ComponentAdapterParent } from "./adapters/component-adapter-parent";
import { SupervisionService } from "src/service/supervision.service";
import { AddressService } from "src/service/address.service";
import { IPromiseSattled } from "../interfaces/IPromiseSattled";
import { Address } from "src/components/shared-components/class/component-classes/address";
import { filter } from "rxjs/operators";
import { SmartComponentType } from "../enum/enumSmartComponentType";
import { GenericCounterMeasurementData } from "./generic-counter-measurements-data";
import { ThemeService } from "src/service/theme.service";
import { SmartPaletteCounter, SmartPaletteGeneral } from "./smart-palette";
import { serverSettings } from "src/state/selectors/server-settings-selector";
import { Store } from "@ngrx/store";
import { LoggerService } from "src/service/logger.service";
import { LocaleService } from "../service/locale.service";
import { LogLevel } from "../enum/enumLog";
import { ActivatedRoute } from "@angular/router";
import { AnimationController } from "@ionic/angular";
import { ViewMode } from "../enum/enumViewMode";
import { Skill } from "./smart-components/smart-component-skill";
import { ImagePosition } from "../enum/enumImagePosition";

/**
 * Parent class to share common function for all widget.
 * This class is abstract and need to be extended by all widget component.
 * It accept a generic type component adapter parent to be used as component adapter
 * and the type of the smart palette of the widget (SmartPaletteCounter for the counter widgets).
 * The empty @Directive() is needed by the AOT build to pass these @Input to every widget.
 */
@Directive()
export abstract class WidgetParent<T extends ComponentAdapterParent, P extends SmartPaletteGeneral = SmartPaletteGeneral> {
  protected _logContext: string = "";
  private _subscriptionAddressUpdate: Subscription = null;
  private _selectServerSettings: Subscription = null;
  protected _idEnvironment: number = null;

  protected readonly _translateService = inject(TranslateService);
  private readonly _activatedRoute = inject(ActivatedRoute);

  protected readonly _smartComponentService = inject(SmartComponentService);
  protected readonly _mqttService = inject(MqttClientService);
  protected readonly _supervisionService = inject(SupervisionService);
  protected readonly _addressService = inject(AddressService);
  protected readonly _themeService = inject(ThemeService);
  protected readonly _elementRef = inject(ElementRef);
  protected readonly _store = inject(Store);
  protected readonly _loggerService = inject(LoggerService);
  protected readonly _localeService = inject(LocaleService);
  private readonly _destroyRef = inject(DestroyRef);
  protected readonly _animationController = inject(AnimationController);
  protected _element: Element = null;
  protected _isExpanded: boolean = false;

  //list of smart component addresses with complete details (useful for label button component)
  protected _addresses: Array<Address> = null;

  protected _listSmartComponentSkills: Array<SmartComponentPropertyDetails> =
    null;
  protected _listSmartComponentCounterMeasurements: Array<GenericCounterMeasurementData> =
    null;
  protected _widgetSmartPalette: P = null;
  protected _widgetIsDisplayable: BehaviorSubject<boolean> = null;
  protected _headerSummary: SmartComponentSummaryData = null;
  protected _errorLoading: boolean = false;

  ///Title of the widget
  public titleWidget: string = "";
  public componentAdapter: T = null;
  public isComponentDisplayable: boolean = false;
  public defaultDecimalDigits: number = 2;

  public classImgPosition: string = "left-orientation";

  //Smart component reference
  @Input() smartComponent: SmartComponent;
  @Input() theme: SmartComponentTheme;
  @Input() isSupervision: boolean;
  @Input() isSupervisionModalOpen: boolean;
  @Input() isPopupHeader: boolean;
  @Input() isSendCommandPossible: boolean;
  @Input() imgPosition: string = "1";
  @Input() showText: boolean;
  @Input() textColor: string = "black";
  @Input() rotationIcon: number;
  @Input() zoom: number;
  //Environment to use when the widget is rendered outside of an environment route (ie. the configuration preview)
  @Input() idEnvironment: number = null;

  public ngOnInit() {
    this._logContext = this.smartComponent?.name;
    this.isComponentDisplayable = false;
    this._widgetIsDisplayable = new BehaviorSubject<boolean>(false);
    //The environment normally comes from the route; when the widget is rendered outside of an
    //environment route (ie. the configuration preview) it is passed as an input instead
    this._idEnvironment =
      this.idEnvironment > 0
        ? this.idEnvironment
        : parseInt(this._activatedRoute.snapshot.paramMap.get("idEnvironment"));

    this.classImgPosition = this.getImgPosition();

    this.loadSettings();

    this._widgetSmartPalette = this.setWidgetSmartPalette();
    this.applySmartPaletteGeneral();

    this.onLanguageChange();
    this._translateService.onLangChange
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(() => this.onLanguageChange());

    this.loadSmartComponentInitialisationData();

    this.onComponentLoad();
  }

  public ngOnDestroy() {
    this._subscriptionAddressUpdate?.unsubscribe();
    this._selectServerSettings?.unsubscribe();
    this.onComponentDestroy();
  }

  /**
   * Load settings from setting service
   */
  private loadSettings(): void {
    this._selectServerSettings = this._store
      .select(serverSettings)
      .pipe(filter((setting) => setting !== null))
      .subscribe((settingsValue) => {
        this.defaultDecimalDigits = settingsValue.DefaultDecimalDigits;
      });
  }

  /**
   * Load data for summary widget
   */
  protected loadSummaryData() {
    this._headerSummary = new SmartComponentSummaryData();
    this._headerSummary.summary = this.smartComponent.summary;
    this._headerSummary.idSmartComponentKind =
      this.smartComponent.idSmartComponentKind;
    this._headerSummary.idEnvironment = this._idEnvironment;
  }

  /**
   * Perform address initial load value for skill state from API
   */
  protected async loadInitialAddressSkillState(): Promise<void> {
    try {
      const arrayPromise: Array<IPromiseSattled> =
        this.componentAdapter.ListSubscribeAddress.map((addressDetails) => {
          return {
            name: addressDetails.instanceGUID + "_" + addressDetails.address,
            promise: this._supervisionService.getSingleAddress(
              addressDetails.instanceGUID,
              addressDetails.address
            ),
          };
        });

      //Use promise all settled (instead of .all) to get all promise result without rejecting all promise in case of one promise fail
      const arrayPromiseResult = await Promise.allSettled(
        arrayPromise.map((promise) => promise.promise)
      );

      arrayPromiseResult.map((promiseResult, index: number) =>
        this.handlePromiseLoadAddressResult(
          arrayPromise[index].name,
          promiseResult
        )
      );

      //Make an array of addresses of complete details for label button component
      this._addresses = arrayPromiseResult.filter(({ status }) => status === 'fulfilled').map((promise: any) => promise.value);

    } catch (exception) {
      this._loggerService.log(
        LogLevel.LOG_ERROR,
        this._logContext,
        this._logContext,
        "loadInitialAddressSkillState()",
        "ID: " + this.smartComponent.id + " EXCEPTION: " + exception?.message
      );
    }
  }

  /**
   *
   * Handles the first application of the values currently present on the skills' addresses we have just susbscribed to.
   *
   * @param keyCall Key of one call INSTANCEGUID_ADDRESS
   * @param promiseResult Promise result of the call
   */
  private handlePromiseLoadAddressResult(
    keyCall: string,
    promiseResult: any
  ): void {
    //Check if promise is fulfilled (resolve successfully)
    if (promiseResult.status === "fulfilled") {
      const address: Address = promiseResult.value as Address;
      if (address.lastValue !== "") {
        this.onAddressValueChange(
          address.instanceGUID,
          address.address,
          address.lastValue
        );
      } else {
        this._loggerService.log(
          LogLevel.LOG_WARNING,
          this._logContext,
          "Widget for " + this.smartComponent?.name,
          "handlePromiseLoadAddressResult()",
          "Error loading address " + keyCall + " - No value found"
        );
      }
    } else {
      this._loggerService.log(
        LogLevel.LOG_ERROR,
        this._logContext,
        "Widget for " + this.smartComponent?.name,
        "handlePromiseLoadAddressResult()",
        "Error loading address " + keyCall
      );
    }
  }

  /**
   * Handles reaction to changes to the values written on the MQTT addresses the widget has ordered the app to susbribe to.
   * In order to do this, it sibscribes to the Subject "addressUpdate" of "Address.service"; then, when it receives an emission, it filters out the updates
   * associated to Addresses that are NOT contained inside the "subscribedAddresses" list of the "Adapter" object created for the specific Widget.
   */
  private performAddressSkillStateSubscription(): void {
    this._subscriptionAddressUpdate = this._addressService.addressUpdate
      .pipe(
        filter((address) => address !== null),
        filter((address) =>
          this.componentAdapter.ListSubscribeAddress.some(
            (addressDetails) =>
              addressDetails.instanceGUID === address.instanceGUID &&
              addressDetails.address === address.address
          )
        )
      )
      .subscribe((address) => {
        this._loggerService.log(
          LogLevel.LOG_ALL,
          this._logContext,
          "Widget-parent for " + this.smartComponent?.name,
          "performAddressSkillStateSubscription()",
          "Address update for: " +
          address.instanceGUID +
          "_" +
          address.address +
          " | value: " +
          address.lastValue
        );

        this.onAddressValueChange(
          address.instanceGUID,
          address.address,
          address.lastValue
        );
      });
  }

  /**
   * Load all the data needed in order to correctly initialise the specific widget, such the list of Skills
   * of the associated SmartComponent object and (in the case the SmartComponent is of "counter" type) its displayed measurements.
   */
  private async loadSmartComponentInitialisationData(): Promise<void> {
    try {
      if (this.isSmartComponentCounter()) {
        await this.loadSmartComponentSkillsMeasurements();
      } else {
        await this.loadSmartComponentSkillsSimple();
      }

      // If component is a counter or a thermostat/sensor/blind in order to set viewMode value
      if (
        this.isSmartComponentCounter() ||
        this.smartComponent.idSmartComponentKind === SmartComponentType.THERMOSTAT ||
        this.smartComponent.idSmartComponentKind === SmartComponentType.SENSOR ||
        this.smartComponent.idSmartComponentKind === SmartComponentType.BLINDS
      ) {
        this._isExpanded =
          this.smartComponent.viewMode === ViewMode.EXPANDED ? true : false;
      }

      if (this.isSupervisionModalOpen) {
        this._isExpanded = true;
      }

      this.onSmartComponentSuccessLoaded();

      /* Here we handle all the addresses associated to the smartComponent */
      if (this.componentAdapter.ListSubscribeAddress.length > 0) {
        await this.loadInitialAddressSkillState();
        this.performAddressSkillStateSubscription();
      } else {
        this._loggerService.log(
          LogLevel.LOG_INFO,
          this._logContext,
          this._logContext,
          "loadSmartComponentInitialisationData()",
          "ID: " +
          this.smartComponent.id +
          " Smartcomponent has no mqtt Addresses associated!"
        );
      }
    } catch (exception) {
      this._loggerService.log(
        LogLevel.LOG_ERROR,
        this._logContext,
        this._logContext,
        "loadSmartComponentInitialisationData()",
        "ID: " + this.smartComponent.id + " EXCEPTION: " + exception?.message
      );

      this.onSmartComponentErrorLoading();
      this._errorLoading = true;
    } finally {
      this.isComponentDisplayable = true;
      this._widgetIsDisplayable.next(this.isComponentDisplayable);
    }
  }

  /**
   * This method (USED IN CASES WHERE THE WIDGET'S ASSOCIATED SMARTCOMPONENT IS OF "COUNTER"-TYPE) loads from
   * server both the Smartcomponent's Skills (and its property-set) and its displayed measurements.
   *
   * Both the errror-handling and the set of actions to be executed in case of succesful termination are localised
   * in the caller mathod.
   */
  private async loadSmartComponentSkillsMeasurements(): Promise<void> {
    if (this.smartComponent.summary) {
      let skill: Skill[] =
        await this._smartComponentService.getTemplateSmartComponent(
          this.smartComponent.idSmartComponentKind
        );
      let smartComponentProperty: SmartComponentProperty =
        new SmartComponentProperty();
      smartComponentProperty.id = -1;
      smartComponentProperty.idSmartComponentKind =
        this.smartComponent.idSmartComponentKind;

      let smartComponentPropertyDetails: SmartComponentPropertyDetails[] =
        skill.map((skill) => {
          return {
            idSmartComponentSkill: skill.id,
            template: skill.template.pattern,
            address: new Address(),
            addressFunction: null,
            isAddressFunction: false,
            id: -1,
            titleSkill: skill.titleSkill,
            shortDescriptionSkill: skill.shortDescriptionSkill,
            dataFormat: skill.dataFormat,
            isWritable: skill.isWritable,
            isRequired: skill.isRequired,
            value: "-1",
            default: skill.default,
          };
        });

      smartComponentProperty.skills = smartComponentPropertyDetails;

      if (!!smartComponentProperty && !!smartComponentProperty?.skills) {
        this._listSmartComponentSkills = smartComponentProperty.skills;
      }

      const counterMeasurementList: GenericCounterMeasurementData[] =
        await this._smartComponentService.getSmartComponentSummaryCounterMeasures(
          this._idEnvironment,
          this.smartComponent.idSmartComponentKind
        );
      this._listSmartComponentCounterMeasurements = counterMeasurementList;
    } else {
      await Promise.all([
        this.loadSmartComponentSkillsSimple(),
        this.loadSmartComponentCounterMeasurements(),
      ]);
    }
  }

  /**
   * Load the "counter"-type smartcomponent's displayed measurements.
   */
  private async loadSmartComponentCounterMeasurements(): Promise<void> {
    try {
      const counterMeasurementList: GenericCounterMeasurementData[] =
        await this._smartComponentService.getSmartComponentCounterMeasures(
          this.smartComponent.id,
          this._idEnvironment
        );
      this._listSmartComponentCounterMeasurements = counterMeasurementList;
    } catch (exception) {
      this._loggerService.log(
        LogLevel.LOG_ERROR,
        this._logContext,
        this._logContext,
        "loadSmartComponentCounterMeasurements()",
        "ID: " + this.smartComponent.id + " EXCEPTION: " + exception?.message
      );
      throw exception;
    }
  }

  /**
   * Load the smart component skills for specific smart component
   */
  private async loadSmartComponentSkillsSimple(): Promise<void> {
    try {
      const smartComponentProperty: SmartComponentProperty =
        await this._smartComponentService.getSmartComponentSkillsByIDSmartComponent(
          this.smartComponent.id,
          this._idEnvironment
        );

      if (!!smartComponentProperty && !!smartComponentProperty?.skills) {
        this._listSmartComponentSkills = smartComponentProperty.skills;
        //Moved to "loadSmartComponentSkills()"
        /*
          this.onSmartComponentSuccessLoaded();
        */
      } else {
        throw new Error(
          "Unable to load smart component or own skills for id " +
          this.smartComponent.id.toString() +
          " . Skills have not been defined or an error has occurred."
        );
      }
    } catch (exception) {
      this._loggerService.log(
        LogLevel.LOG_ERROR,
        this._logContext,
        this._logContext,
        "loadSmartComponentSkillsSimple()",
        "ID: " + this.smartComponent.id + " EXCEPTION: " + exception?.message
      );
      throw exception;
    }
  }

  /**
   * This method checks if the currently considered widget is associated with a "counter"-type SmartComponent.
   * @returns TRUE if the SmartComponent is of "counter"-type, FALSE otherwise.
   */
  private isSmartComponentCounter(): boolean {
    const kind: SmartComponentType = this.smartComponent.idSmartComponentKind;
    return (
      kind == SmartComponentType.ELECTRIC ||
      kind == SmartComponentType.HOT_WATER ||
      kind == SmartComponentType.COLD_WATER ||
      kind == SmartComponentType.HEATING ||
      kind == SmartComponentType.DISTRIBUTOR ||
      kind == SmartComponentType.GAS
    );
  }

  /**
   * Send generic address command value when skill is commanded
   *
   * @param instancdeGUID Instance GUID of the to command
   * @param address Address to command
   * @param value Value to send over address
   */
  protected sendAddressCommand(
    instancdeGUID: string,
    address: string,
    value: string
  ) {
    this._mqttService.publishAddressWrite(
      instancdeGUID + "_" + address,
      instancdeGUID,
      address,
      "",
      true,
      value
    );
  }

  /**
   * Writes again the texts the widget builds in the code, such as the names of the months. Called when the widget
   * starts and every time the language changes.
   */
  protected onLanguageChange(): void {}

  /**
   * This method set the "main palette" of the widget, colors which will be used to style its main graphic components
   * (IE. title lables,sub-titles lables,  general background...). The exact colours are decided in the specific widget,
   *  based on its own internal logic.
   */
  protected abstract setWidgetSmartPalette(): P;

  /**
   * This method gives a "default" example of the "main palette" of the "generic" widget, colors which will be used to style its main graphic component
   * (IE. title lables,sub-titles lables,  general background...).
   */
  protected setWidgetSmartPaletteGeneralDefault(): SmartPaletteGeneral {
    const smartPalette = new SmartPaletteGeneral();

    smartPalette.iconColorLighter = "#0086D2";
    smartPalette.sliderColorLighter = "#E4E4E4";
    smartPalette.sliderColorDarker = "#E78787";

    return smartPalette;
  }

  /**
   * This method gives a "default" example of the "main palette" of the "counter"-type widget, colors which will be used to style its main graphic component
   * (IE. title lables,sub-titles lables,  general background...).
   */
  protected setWidgetSmartPaletteCounterDefault(): SmartPaletteCounter {
    const smartPalette = new SmartPaletteCounter();

    const brand = this.resolveBrandColors();

    // Vimar uses a distinct light look (light surface, dark label-color text, amber
    // primary as accent), so it has its own palette branch.
    if (this._themeService?.applicationTheme === ApplicationTheme.VIMAR) {
      return this.buildVimarCounterPalette(smartPalette, brand);
    }

    const graphAccent = this.getBrandGraphAccent();

    smartPalette.iconColorLighter = brand.primary;
    smartPalette.sliderColorLighter = "#E4E4E4";
    smartPalette.sliderColorDarker = "#E78787";
    smartPalette.backgroundColor = brand.primary;
    smartPalette.mainLableColor = brand.contrast;
    smartPalette.secondaryLableColor = `rgba(${brand.contrastRgb}, 0.7)`;
    smartPalette.bandDoubleBackground = `rgba(${brand.contrastRgb}, 0.2)`;
    smartPalette.bandTripleBackground = `rgba(${brand.contrastRgb}, 0.3)`;
    smartPalette.expandableSpanBackground = smartPalette.mainLableColor;
    smartPalette.expandableSpanColor = smartPalette.backgroundColor;
    smartPalette.graphBar1Color = brand.contrast;
    smartPalette.graphBar2Color = graphAccent;
    smartPalette.graphLineMin = brand.tint;
    smartPalette.graphPointMin = brand.contrast;
    smartPalette.graphLineMax = brand.shade;
    smartPalette.graphPointMax = brand.shade;
    smartPalette.buttonBackground = brand.contrast;
    smartPalette.buttonTextColor = brand.primary;

    return smartPalette;
  }

  /**
   * Vimar-specific counter palette: a light surface (--ion-color-light) with dark
   * --label-color text and the amber primary used as graph accent. Colors consumed by
   * ECharts are resolved to concrete values; CSS-only fields can keep var(...) references.
   */
  private buildVimarCounterPalette(
    smartPalette: SmartPaletteCounter,
    brand: {
      primary: string;
      shade: string;
      tint: string;
      contrast: string;
      contrastRgb: string;
    }
  ): SmartPaletteCounter {
    const styles = this.getBrandStyles();
    const labelColor =
      styles.getPropertyValue("--label-color").trim() || "#464646";
    const labelColorRgb =
      styles.getPropertyValue("--label-color-rgb").trim() || "70, 70, 70";
    const accent = brand.primary; // resolved --ion-color-primary (amber)

    smartPalette.iconColorLighter = accent;
    smartPalette.sliderColorLighter = "#E4E4E4";
    smartPalette.sliderColorDarker = "#E78787";
    smartPalette.backgroundColor = "var(--ion-color-light)";
    smartPalette.mainLableColor = labelColor;
    smartPalette.secondaryLableColor = `rgba(${labelColorRgb}, 0.7)`;
    smartPalette.bandDoubleBackground = `rgba(${labelColorRgb}, 0.08)`;
    smartPalette.bandTripleBackground = `rgba(${labelColorRgb}, 0.14)`;
    smartPalette.expandableSpanBackground = labelColor;
    smartPalette.expandableSpanColor = "var(--ion-color-light)";
    smartPalette.graphBar1Color = labelColor;
    smartPalette.graphBar2Color = accent;
    smartPalette.graphLineMin = `rgba(${labelColorRgb}, 0.4)`;
    smartPalette.graphPointMin = labelColor;
    smartPalette.graphLineMax = brand.shade;
    smartPalette.graphPointMax = accent;
    smartPalette.buttonBackground = "var(--ion-color-light)";
    smartPalette.buttonTextColor = labelColor;

    return smartPalette;
  }

  /**
   * Resolve the current brand theme colors (--ion-color-primary and its shades) to
   * concrete color values. This is needed because some palette colors are consumed
   * inside the ECharts configuration, which does NOT resolve CSS "var(...)" expressions.
   * Falls back to the legacy "Digital Place" blue palette if a variable is missing.
   */
  private resolveBrandColors(): {
    primary: string;
    shade: string;
    tint: string;
    contrast: string;
    contrastRgb: string;
  } {
    const styles = this.getBrandStyles();
    const read = (name: string, fallback: string): string => {
      const value = styles.getPropertyValue(name).trim();
      return value !== "" ? value : fallback;
    };

    return {
      primary: read("--ion-color-primary", "#0086D2"),
      shade: read("--ion-color-primary-shade", "#035E91"),
      tint: read("--ion-color-primary-tint", "#d8ede7"),
      contrast: read("--ion-color-primary-contrast", "#ffffff"),
      contrastRgb: read("--ion-color-primary-contrast-rgb", "255, 255, 255"),
    };
  }

  /**
   * Computed styles the brand theme colors are read from.
   * They are taken from the document body
   */
  private getBrandStyles(): CSSStyleDeclaration {
    return getComputedStyle(document.body);
  }

  /**
   * Accent color for the "current year" graph series (graphBar2 / pie base color),
   * chosen per brand so it stays distinguishable from the primary-derived series.
   * This is the single place to tweak the per-brand graph accent.
   */
  private getBrandGraphAccent(): string {
    switch (this._themeService?.applicationTheme) {
      case ApplicationTheme.DIGITAL_PLACE:
        return "#035E91";
      case ApplicationTheme.BIG:
        return "#A8DB3C";
      case ApplicationTheme.BLUMOTIX:
      case ApplicationTheme.MDT:
      default:
        return "#F2A93B";
    }
  }

  /**
   * Load component procedure start
   */
  protected abstract onComponentLoad(): void;

  /**
   * This abstract method contains all the specific actions the particular SmartComponent (IE. the specific
   * descendent-class) must execute after the succesfull fetching from server of its current property-set
   * (IE: the current "valorization-mask" to be applied to its Skill-set).
   *
   * In the peculiar case of "counter"-type smartcomponnets, this also inlcudes the fetching of the counter's measurements.
   *
   * Usually, these actions should include the creation and the initilisation (with the fetched property-set) of
   * the specific componentAdapter object associated with the widget.
   *
   */
  protected abstract onSmartComponentSuccessLoaded(): void;

  /**
   * Destroy component procedure
   */
  protected abstract onComponentDestroy(): void;

  /**
   * Handle smart component error generic loading; in the peculiar case of "counter"-type smartcomponnets, this also
   * includes errors in fetching the counter's measurements.
   */
  protected abstract onSmartComponentErrorLoading(): void;

  /**
   * Address value change event handler
   *
   * @param instanceGUID Instance guid of address changed
   * @param address address changed
   * @param value new value of address changed
   */
  protected abstract onAddressValueChange(
    instanceGUID: string,
    address: string,
    value: string
  ): void;

  /**
   * This method apply to the html template of the current widget all the customizable theme-related values, as defined
   * inside the specific "Widget-***.component" class and passed through a "SmartPalette" object.
   *
   * If the specific type of widget offers further customizable graphical elements, then additional and more specific method shall be called
   * based on the type of the "SmartPalette" previously defined
   */
  private applySmartPaletteGeneral(): void {
    this._elementRef.nativeElement.style.setProperty(
      "--dimmer-color",
      this._widgetSmartPalette.iconColorDarker
    );
    this._elementRef.nativeElement.style.setProperty(
      "--slider-light-color",
      this._widgetSmartPalette.sliderColorLighter
    );
    this._elementRef.nativeElement.style.setProperty(
      "--slider-dark-color",
      this._widgetSmartPalette.sliderColorDarker
    );

    if (this._widgetSmartPalette instanceof SmartPaletteCounter) {
      this.applySmartPaletteCounter(this._widgetSmartPalette);
    }
  }

  /**
   * This method apply to the html template of the current "counter-type" widget all the customizable theme-related values, as defined
   * inside the specific "Widget-counter-***.component" class and passed through a "SmartPalette" object.
   * @param paletteCounter The SmartPaletteCounter object containing the customized values to apply.
   */
  private applySmartPaletteCounter(paletteCounter: SmartPaletteCounter): void {
    this._elementRef.nativeElement.style.setProperty(
      "--smart-background",
      paletteCounter.backgroundColor
    );
    this._elementRef.nativeElement.style.setProperty(
      "--smart-color-main",
      paletteCounter.mainLableColor
    );
    this._elementRef.nativeElement.style.setProperty(
      "--smart-color-secondary",
      paletteCounter.secondaryLableColor
    );
    this._elementRef.nativeElement.style.setProperty(
      "--smart-band-double-background",
      paletteCounter.bandDoubleBackground
    );
    this._elementRef.nativeElement.style.setProperty(
      "--smart-band-triple-background",
      paletteCounter.bandTripleBackground
    );

    //TO BE MOVED TO INNER "EXPANDABLE" component?
    this._elementRef.nativeElement.style.setProperty(
      "--smart-expandable-background",
      paletteCounter.expandableSpanBackground
    );
    this._elementRef.nativeElement.style.setProperty(
      "--smart-expandable-color",
      paletteCounter.expandableSpanColor
    );
  }

  /**
   * Get the class according to the value for orientation of icon image and text
   * @returns string with name of the class
   */
  protected getImgPosition(value = this.imgPosition): string {
    switch (value) {
      case ImagePosition.LEFT:
        return "left-orientation";
      case ImagePosition.RIGHT:
        return "right-orientation"
      case ImagePosition.UP:
        return "up-orientation";
      case ImagePosition.DOWN:
        return "down-orientation";
    }
  }
}
